import { FilterBar } from '@/components/filters';
import { AddFilterButtonDropdown } from '@/components/filters/AddFilterButton';
import { AppliedDataFilter } from '@/components/filters/FilterButton';
import { Box, Button, Card, RadioGroup, useBreakpoint } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { UserPermissions } from 'types';

import useIsIntegratedPartnerUser from '../../../hooks/useIsIntegratedPartnerUser';
import {
  useGetOrganizationListFiltersQuery,
  useLazySearchOrganizationsByUserQuery,
  useLazySearchOrganizationsQuery,
} from '../../../services/organizations';
import RequirePermissions from '../../auth/requirePermissions';
import { MainContainer } from '../../MainContainer';
import PageHeader from '../../PageHeader';

import { OrganizationsListLoading } from './components/OrganizationListLoading';
import OrganizationListRow from './OrganizationListRow';

export function Organizations() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [appliedFilters, setAppliedFilters] = useState<Array<any>>([]);
  const [filteredTree, setFilteredTree] = useState<any[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchType, setSearchType] = useState('organization');

  const advancedFilters = searchParams.get('advancedFilters');
  const searchTerm = searchParams.get('search') || '';

  const { data: filters = [], isLoading: isFiltersLoading } = useGetOrganizationListFiltersQuery({});
  const [triggerOrganizationTreeQuery, { data: treeData, isLoading: isOrganizationTreeLoading }] =
    useLazySearchOrganizationsQuery({});
  const [triggerUserSearch, { data: userOrganizationsData, isLoading: isUserOrganizationsLoading }] =
    useLazySearchOrganizationsByUserQuery();

  const { isPhone } = useBreakpoint();
  const isIntegratedPartnerUser = useIsIntegratedPartnerUser();
  const navigate = useNavigate();

  const isLoading = isOrganizationTreeLoading || isUserOrganizationsLoading || isSearchLoading;

  const onSearchChange = (searchTerm: string) => {
    setFilteredTree([]);
    setIsSearchLoading(true);
    if (searchTerm && searchTerm !== '') {
      searchParams.set('search', searchTerm);
    } else {
      searchParams.delete('search');
    }
    setSearchParams(searchParams);
  };

  const handleApplyAdvancedFilters = useCallback(
    (filtersToApply: AppliedDataFilter[]) => {
      if (filtersToApply.some((filter) => filter.selectedValues && filter.selectedValues.length)) {
        searchParams.set('advancedFilters', JSON.stringify(filtersToApply));
        setSearchParams(searchParams);
      }
      setAppliedFilters(filtersToApply);
    },
    [setAppliedFilters, searchParams, setSearchParams],
  );

  const handleRemoveAdvancedFilter = useCallback(
    (filterId: string) => {
      const newFilters = appliedFilters.filter((filter) => filter.id !== filterId);
      if (newFilters.length) {
        searchParams.set('advancedFilters', JSON.stringify(newFilters));
      } else {
        searchParams.delete('advancedFilters');
      }
      setSearchParams(searchParams);
      setAppliedFilters(newFilters);
    },
    [appliedFilters, setAppliedFilters, searchParams, setSearchParams],
  );

  const handleClearAdvancedFilters = useCallback(() => {
    searchParams.delete('advancedFilters');
    setSearchParams(searchParams);
    setAppliedFilters([]);
  }, [setAppliedFilters, searchParams, setSearchParams]);

  const handleSearchTypeChange = (event: any) => {
    const newSearchType = event.target.value;
    setSearchType(newSearchType);
    setFilteredTree([]);
    setIsSearchLoading(true);
    onSearchChange('');
    triggerOrganizationTreeQuery({
      searchTerm: '',
      advancedFilters: advancedFilters || JSON.stringify([]),
      useRootOrganization: false,
    }).finally(() => setIsSearchLoading(false));
  };

  useEffect(() => {
    if (searchType === 'user' && !searchTerm.trim()) {
      setFilteredTree(treeData?.organizations || []);
      setIsSearchLoading(false);
    } else if (searchType === 'user' && searchTerm.trim() && userOrganizationsData) {
      setFilteredTree(userOrganizationsData.organizations);
      setIsSearchLoading(false);
    } else if (searchType === 'organization' && treeData) {
      setFilteredTree(treeData.organizations);
      setIsSearchLoading(false);
    }
  }, [searchType, searchTerm, userOrganizationsData, treeData]);

  useEffect(() => {
    const filters = advancedFilters || JSON.stringify([]);
    setAppliedFilters(JSON.parse(filters));

    if (searchType === 'organization') {
      setIsSearchLoading(true);
      triggerOrganizationTreeQuery({ searchTerm: searchTerm, advancedFilters: filters }).finally(() =>
        setIsSearchLoading(false),
      );
    } else if (searchType === 'user' && searchTerm.trim()) {
      setIsSearchLoading(true);
      triggerUserSearch({ search: searchTerm }).finally(() => setIsSearchLoading(false));
    }
  }, [searchType, searchTerm, advancedFilters, triggerOrganizationTreeQuery, triggerUserSearch]);

  return (
    <>
      <Helmet>
        <title>Organizations | Settings</title>
      </Helmet>
      <MainContainer>
        <PageHeader title="Organizations" description="All visible organizations for the current organization">
          {!isIntegratedPartnerUser && (
            <RequirePermissions permissions={[UserPermissions.admin]}>
              <Button
                as="a"
                navigate={() => navigate('/settings/organizations/new')}
                variant="primary"
                size={{ base: 'sm', desktop: 'md' }}
                aria-label="create Organization"
                iconPrefix="add"
              >
                {!isPhone && 'Organization'}
              </Button>
            </RequirePermissions>
          )}
        </PageHeader>
        <Box>
          <Box background="tertiary" radius="md md 0 0">
            <FilterBar
              filtersState={{
                searchTerm,
              }}
              handleSearchTerm={(searchTerm: string) => onSearchChange(searchTerm)}
              placeholder={searchType === 'organization' ? 'Search org by name or alias' : 'Search org by user'}
            >
              <Box direction="row" style={{ flexGrow: 1 }}>
                {!isFiltersLoading && searchType !== 'user' && (
                  <AddFilterButtonDropdown
                    dataFilters={filters}
                    appliedFilters={appliedFilters}
                    onRemoveFilter={handleRemoveAdvancedFilter}
                    onApplyFilters={handleApplyAdvancedFilters}
                    onClearFilters={handleClearAdvancedFilters}
                  />
                )}
              </Box>
            </FilterBar>
            <Box margin="md md md lg">
              <RadioGroup
                title="Search By"
                direction="row"
                name="searchType"
                onChange={handleSearchTypeChange}
                value={searchType}
                options={[
                  { id: 'organization', value: 'organization', label: 'Organization' },
                  { id: 'user', value: 'user', label: 'User' },
                ]}
              />
            </Box>
          </Box>
          {isLoading ? (
            <Card radius="0 0 md md">
              <OrganizationsListLoading rows={25} />
            </Card>
          ) : filteredTree && filteredTree.length ? (
            <Card radius="0 0 md md">
              {filteredTree.map((item, index) => (
                <OrganizationListRow key={`${item.id}-${index}`} item={item} level={0} />
              ))}
            </Card>
          ) : (
            <Card radius="0 0 md md" padding="lg" textAlign="center">
              {searchType === 'organization'
                ? 'No organizations match the current search and/or filter(s)'
                : 'No organization match searched user'}
            </Card>
          )}
        </Box>
      </MainContainer>
    </>
  );
}
